/* eslint-disable max-len,array-bracket-newline */
import {
    useCallback,
    useMemo,
} from 'react'
import {
    GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro'
import {
    useTheme,
} from '@mui/material/styles'

import merge from 'lodash/merge'

import useWindowSize from 'app/hooks/useWindowSize'
import {
    ExtendedGridColDef,
    SkyDataGridLocalStorage,
} from '../../SkyBaseDataGrid.types'
import {
    hideHiddenColumnsForFilter,
    getNotHiddenDefinitions,
} from '../../utils/FilterModel.utils'
import usePersistedTableWidthPercent from '../usePersistedTableWidthPercent'
import useSkyDataGridLocalStorage from '../useSkyDataGridLocalStorage'
import {
    DEFAULT_MAIN_TABLE_IN_DETAILS_VIEW_WIDTH_PERCENT,
    getTargetVisibleColumnsCount,
    getOnlyFirstColumnVisibilityModel,
    getFirstReducedColumnVisibilityModel,
    getOnlyReducedColumnsVisibilityModel,
    getCurrentlyVisibleColumnsNames,
} from './visibilityModel.utils'

type Props = {
    columns: ExtendedGridColDef[],
    persistentKey: string,
    isDetailsView: boolean,
    initialTableWidthPercent?: number,
}

type ReturnType = {
    visibilityModel: GridColumnVisibilityModel,
    onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void,
    tableWidthPercent: number,
    setTableWidthPercent: (percent: number) => void,
}

const usePersistedVisibleColumnsWithDetailsViewAndResize = ({
    columns,
    persistentKey,
    isDetailsView,
    initialTableWidthPercent = DEFAULT_MAIN_TABLE_IN_DETAILS_VIEW_WIDTH_PERCENT,
}: Readonly<Props>): ReturnType => {
    const {
        width,
    } = useWindowSize()
    const {
        breakpoints,
    } = useTheme()
    const widthBreakPoint = breakpoints.values.xl
    const setStateConverter = useCallback((value: GridColumnVisibilityModel) => {
        return isDetailsView ? {
            detailsView: {
                visibilityModel: {
                    ...value,
                },
            },
        } : {
            visibilityModel: {
                ...value,
            },
        }
    }, [isDetailsView])
    const initialVisibilityModel = useMemo(() => {
        return hideHiddenColumnsForFilter(columns)
    }, [columns])
    const hideNotReducedColumnsModel = useMemo(() => {
        return getOnlyReducedColumnsVisibilityModel(columns)
    }, [columns])
    const hasReducedColumns = useMemo(() => {
        return Object.values(hideNotReducedColumnsModel).includes(true)
    }, [hideNotReducedColumnsModel])
    const getDefaultInitialVisibilityModelForDetailsView = useCallback((): GridColumnVisibilityModel => {
        /* if no reduced columns return first column only */
        if (!hasReducedColumns) {
            return getOnlyFirstColumnVisibilityModel(columns)
        }
        /* if small screen return only first reduced columns */
        if (width < widthBreakPoint) {
            return getFirstReducedColumnVisibilityModel(columns)
        }
        /* for large screens return all reduced columns */
        const result = {
            ...initialVisibilityModel,
            ...hideNotReducedColumnsModel,
        }

        return result
    }, [
        columns,
        width,
        initialVisibilityModel,
        widthBreakPoint,
        hideNotReducedColumnsModel,
        hasReducedColumns,
    ])
    const localStorageInitState = useMemo(() => {
        const result = isDetailsView ? {
            detailsView: {
                visibilityModelByResize: {
                    ...getDefaultInitialVisibilityModelForDetailsView(),
                },
            },
        } : {
            visibilityModel: {
                ...initialVisibilityModel,
            },
        }

        return result
    }, [
        isDetailsView,
        initialVisibilityModel,
        getDefaultInitialVisibilityModelForDetailsView,
    ])
    const {
        dataGridState,
        setDataGridState,
        getStorageValue,
    } = useSkyDataGridLocalStorage<SkyDataGridLocalStorage>(persistentKey, localStorageInitState)
    const noAutoResizeChanges = useMemo(() => {
        return Boolean(dataGridState?.detailsView?.visibilityModel)
    }, [
        dataGridState,
    ])
    const visibilityModel = useMemo(() => {
        let result = dataGridState?.visibilityModel || initialVisibilityModel

        if (isDetailsView) {
            result = dataGridState?.detailsView?.visibilityModel
                || dataGridState?.detailsView?.visibilityModelByResize
                || getDefaultInitialVisibilityModelForDetailsView()
        }
        return result
    }, [
        dataGridState?.detailsView?.visibilityModel,
        dataGridState?.detailsView?.visibilityModelByResize,
        dataGridState?.visibilityModel,
        initialVisibilityModel,
        getDefaultInitialVisibilityModelForDetailsView,
        isDetailsView,
    ])

    const onColumnVisibilityModelChangeForResize = useCallback((value: GridColumnVisibilityModel) => {
        const newState = {
            ...dataGridState,
            ...({
                detailsView: {
                    visibilityModelByResize: {
                        ...value,
                    },
                },
            }),
        }

        setDataGridState(newState)
    }, [
        setDataGridState,
        dataGridState,
    ])

    const onResizeChange = useCallback((newTableWithPercent: number) => {
        if (noAutoResizeChanges) return
        const targetVisibleColumnsCount = getTargetVisibleColumnsCount(newTableWithPercent, initialTableWidthPercent, columns.length)
        const currentlyVisibleColumns = getCurrentlyVisibleColumnsNames(visibilityModel)

        /* adding columns */
        if (targetVisibleColumnsCount > currentlyVisibleColumns.length) {
            const notShownColumns = getNotHiddenDefinitions(columns)
                .filter((column) => {
                    return (column.reduced || !hasReducedColumns)
                            && !currentlyVisibleColumns.includes(column.name as string)
                })
                .map((column) => { return column.name })

            if (notShownColumns.length > 0) {
                const newVisibilityModel = {
                    ...visibilityModel,
                    [notShownColumns[0]]: true,
                }

                onColumnVisibilityModelChangeForResize(newVisibilityModel)
            }
        /* removing columns */
        } else if (targetVisibleColumnsCount < currentlyVisibleColumns.length) {
            const columnsToHide = currentlyVisibleColumns.slice(-1 * (currentlyVisibleColumns.length - targetVisibleColumnsCount))
            const newVisibilityModel = {
                ...visibilityModel,
                ...Object.fromEntries(
                    columnsToHide.map((columnName) => {
                        return [
                            columnName,
                            false,
                        ]
                    }),
                ),
            }

            onColumnVisibilityModelChangeForResize(newVisibilityModel)
        }
    }, [
        initialTableWidthPercent,
        visibilityModel,
        columns,
        onColumnVisibilityModelChangeForResize,
        hasReducedColumns,
        noAutoResizeChanges,
    ])

    const [
        tableWidthPercent,
        setTableWidthPercent,
    ] = usePersistedTableWidthPercent({
        persistentKey,
        onChange: onResizeChange,
    })

    const onColumnVisibilityModelChange = useCallback((value: GridColumnVisibilityModel) => {
        const currentDataGridState = getStorageValue()
        const newValue = setStateConverter(value)
        const newState = merge({}, currentDataGridState, newValue)

        setDataGridState(newState)
    }, [
        setStateConverter,
        setDataGridState,
        getStorageValue,
    ])

    return {
        visibilityModel,
        onColumnVisibilityModelChange,
        tableWidthPercent,
        setTableWidthPercent,
    }
}

export default usePersistedVisibleColumnsWithDetailsViewAndResize
